import React, { useCallback, useId, useState } from 'react';
import { SendLimit, setEffectCapacity } from '../../../api/cafAutomation';
import { Modal } from '../Overlay';
import { isClash, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { NoteField, SaveProblem } from './formBits';

export const DAYS_KEY = 'cur-automation-days';
const WINDOWS = [7, 14, 30];
const SEND_LIMIT_REASON = 'Changed in Automation settings';

/** Per-viewer convenience only: blocked or cleared storage just means the default, 7 days. */
export const readDays = (): number => {
  try {
    const value = Number(window.localStorage.getItem(DAYS_KEY));
    return WINDOWS.indexOf(value) !== -1 ? value : 7;
  } catch {
    return 7;
  }
};

export const useDaysSetting = (): [number, (days: number) => void] => {
  const [days, setDays] = useState(readDays);
  const choose = useCallback((next: number) => {
    setDays(next);
    try {
      window.localStorage.setItem(DAYS_KEY, String(next));
    } catch {
      // Private window or blocked storage: the choice lasts for this page view only.
    }
  }, []);
  return [days, choose];
};

/** The backend also sends the allowed range; fall back to 1-4 when it is missing. */
type LimitInfo = SendLimit & { min_limit?: number; max_limit?: number };

interface Props {
  sendLimit: LimitInfo | null;
  days: number;
  onDays: (days: number) => void;
  onClose: () => void;
  onSaved: () => void;
  onTechnical: () => void;
}

export const SettingsModal: React.FC<Props> = ({ sendLimit, days, onDays, onClose, onSaved, onTechnical }) => {
  const id = useId();
  const toast = useToast();
  const [limit, setLimit] = useState(sendLimit ? sendLimit.slot_limit : 1);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const min = sendLimit?.min_limit ?? 1;
  const max = sendLimit?.max_limit ?? 4;
  const options: number[] = [];
  for (let value = min; value <= max; value += 1) options.push(value);
  if (sendLimit && options.indexOf(sendLimit.slot_limit) === -1) options.push(sendLimit.slot_limit);
  const changed = Boolean(sendLimit) && limit !== sendLimit?.slot_limit;

  const save = async () => {
    if (!sendLimit) return;
    setSaving(true);
    setProblem('');
    const result = await runSave(() => setEffectCapacity({
      slot_limit: limit, expected_revision: sendLimit.revision, reason: note.trim() || SEND_LIMIT_REASON,
    }));
    setSaving(false);
    if (!result.ok) {
      setProblem(result.message);
      if (!isClash(result.message)) toast.failed(result.message, () => { void save(); });
      return;
    }
    toast.saved('Saved.');
    onSaved();
    onClose();
  };

  return (
    <Modal title="Settings" onClose={onClose} busy={saving}
      footer={(
        <>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={saving}>
            {changed ? 'Cancel' : 'Close'}
          </button>
          {changed && (
            <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={saving} onClick={() => { void save(); }}>
              {saving ? <><span className="cur-spin" aria-hidden="true" />Saving…</> : 'Save'}
            </button>
          )}
        </>
      )}>
      <div className="cur-field">
        <label htmlFor={`${id}-limit`}>How many client emails may go out at a time</label>
        <select id={`${id}-limit`} className="cur-input" value={limit} disabled={!sendLimit || saving}
          onChange={(event) => setLimit(Number(event.target.value))}>
          {options.map((value) => <option key={value} value={value}>{value < min || value > max ? `${value} (current)` : value}</option>)}
        </select>
        {sendLimit
          ? <span className="cur-hint">This is how many go out together, not a daily limit. Lowering it never stops emails already going out.</span>
          : <span className="cur-hint">Couldn&apos;t read how many client emails may go out at a time.</span>}
      </div>
      {changed && <NoteField id={`${id}-note`} value={note} onChange={setNote} disabled={saving} />}
      <SaveProblem message={problem} onReload={() => { setProblem(''); onSaved(); onClose(); }} />
      <div className="cur-field">
        <label htmlFor={`${id}-days`}>Days of history to show</label>
        <select id={`${id}-days`} className="cur-input" value={days} onChange={(event) => onDays(Number(event.target.value))}>
          {WINDOWS.map((value) => <option key={value} value={value}>{`${value} days`}</option>)}
        </select>
        <span className="cur-hint">It only changes what you see.</span>
      </div>
      <p className="cur-hint">Which AI model reads the emails (Haiku or palantir) is a technical setting on Admin › Processors.</p>
      <div><button type="button" className="cur-linkish" onClick={onTechnical}>Technical details</button></div>
    </Modal>
  );
};
